import { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { textToSpeechStyles } from './TextToSpeech.styles';

export interface TextToSpeechItem {
  id: number | string;
  text: string;
}

export interface TextToSpeechProps {
  items: TextToSpeechItem[];
  questionId?: number | string;
  getPlaybackItems?: () => TextToSpeechItem[];
  onStart?: () => void;
  onEnd?: () => void;
  onItemPlay?: (id: number | string) => void;
}

export const TextToSpeech = ({ items, questionId, getPlaybackItems, onStart, onEnd, onItemPlay }: TextToSpeechProps) => {
  const isSpeechSupported = typeof window !== 'undefined' &&
    'speechSynthesis' in window &&
    'SpeechSynthesisUtterance' in window;
  const theme = useTheme();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const hasCalledOnStart = useRef(false);
  // Quiz updates may replace items without navigating to a different question.
  const questionPlaybackKey = questionId ?? items;

  const speakItem = useCallback((index: number, playbackItems: TextToSpeechItem[]) => {
    if (index >= playbackItems.length) {
      setIsPlaying(false);
      setIsPaused(false);
      hasCalledOnStart.current = false;
      onEnd?.();
      return;
    }

    const text = playbackItems[index]?.text || '';
    if (!text) {
      speakItem(index + 1, playbackItems);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;

    utterance.onstart = () => {
      if (!hasCalledOnStart.current) {
        hasCalledOnStart.current = true;
        onStart?.();
      }
      onItemPlay?.(playbackItems[index].id);
    };

    utterance.onend = () => {
      speakItem(index + 1, playbackItems);
    };

    utterance.onerror = (event) => {
      if (event.error !== 'interrupted' && event.error !== 'canceled') {
        speakItem(index + 1, playbackItems);
      }
    };

    window.speechSynthesis.speak(utterance);
  }, [onStart, onEnd, onItemPlay]);

  useEffect(() => {
    if (!isSpeechSupported) {
      return undefined;
    }

    setIsPlaying(false);
    setIsPaused(false);
    hasCalledOnStart.current = false;

    return () => {
      if (utteranceRef.current) {
        utteranceRef.current.onstart = null;
        utteranceRef.current.onend = null;
        utteranceRef.current.onerror = null;
        utteranceRef.current = null;
      }
      window.speechSynthesis.cancel();
    };
  }, [questionPlaybackKey, isSpeechSupported]);

  if (!isSpeechSupported) {
    return null;
  }

  const handleClick = () => {
    if (isPlaying && !isPaused) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    } else if (isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    } else {
      const playbackItems = (getPlaybackItems ? getPlaybackItems() : items)
        .map(item => ({ id: item.id, text: item.text }));
      window.speechSynthesis.cancel();
      hasCalledOnStart.current = false;
      setIsPlaying(true);
      setIsPaused(false);
      speakItem(0, playbackItems);
    }
  };

  return (
    <Box sx={textToSpeechStyles.container(theme)}>
      <Typography sx={textToSpeechStyles.title(theme)}>
        Text-to-Speech
      </Typography>
      <Button
        variant="contained"
        onClick={handleClick}
        startIcon={isPlaying && !isPaused ? <PauseIcon /> : <PlayArrowIcon />}
        sx={textToSpeechStyles.button(theme)}
      >
        {isPlaying && !isPaused ? 'Pause' : 'Play'}
      </Button>
    </Box>
  );
};

export default TextToSpeech;
